import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axiosInstance from '../../../libs/axios';
import { useAuth } from '../../../provider/auth.provider';
import {
  FiInfo,
  FiMapPin,
  FiUsers,
  FiCalendar,
  FiUser,
  FiMail,
  FiPhone,
  FiAlertCircle,
  FiFileText,
  FiBriefcase,
  FiFile,
  FiDownload,
  FiExternalLink,
} from 'react-icons/fi';

// Types
interface StateCircle {
  id: number;
  state_name: string;
  circle_name: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
}

interface Representative {
  id: number;
  vendor_id: number;
  contact_person: string;
  contact_number: string;
  designation_id: number;
  representative_location: string;
  email: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface VendorDocument {
  name: string;
  url: string;
  size?: number;
  mime_type?: string;
}

interface ProjectAssociation {
  project_id: number;
  project?: { id: number; project_name: string };
}

interface ActivityAssociation {
  activity_id: number;
  activity?: { id: number; activity_name: string };
}

interface Vendor {
  id: number;
  vendor_full_name: string;
  vendor_short_name: string;
  vendor_code?: string;
  address: string;
  gst_number: string;
  unregistered_gst?: string;
  pan_number?: string;
  bank_name?: string;
  bank_account_number?: string;
  bank_ifsc_code?: string;
  bank_address?: string;
  state_circle_id: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  created_by: number;
  suspended_at: string | null;
  suspended_by: number | null;
  representatives: Representative[];
  state_circle: StateCircle;
  project_associations?: ProjectAssociation[];
  activity_associations?: ActivityAssociation[];
  documents?: VendorDocument[];
}

interface VendorData {
  vendor: Vendor;
}

interface TabProps {
  data: VendorData;
}

// Vendor Overview Tab Component
const VendorOverviewTab: React.FC<TabProps> = ({ data }) => {
  const { vendor } = data;

  const getStatusColor = (isActive: boolean): string => {
    return isActive ? 'text-green-600 bg-green-100' : 'text-red-600 bg-red-100';
  };

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-base font-semibold text-gray-900">Vendor Information</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Full Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiUser className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.vendor_full_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Short Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.vendor_short_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">GST Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.gst_number}</span>
              </div>
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">State & Circle</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiMapPin className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.state_circle.state_name} - {vendor.state_circle.circle_name}</span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <div className={`w-2 h-2 rounded-full ${vendor.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getStatusColor(vendor.is_active)}`}>
                  {vendor.is_active ? 'ACTIVE' : 'INACTIVE'}
                </span>
              </div>
            </div>

            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Created Date</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiCalendar className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{new Date(vendor.created_at).toLocaleDateString()}</span>
              </div>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <label className="block text-xs font-medium text-gray-600 mb-1">Address</label>
          <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
            <FiMapPin className="text-gray-400 mt-0.5" size={14} />
            <span className="text-sm text-gray-900">{vendor.address}</span>
          </div>
        </div>

        {vendor.project_associations && vendor.project_associations.length > 0 && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-2">Projects</label>
            <div className="flex flex-wrap gap-1">
              {vendor.project_associations.map((pa) => (
                <span key={pa.project_id} className="inline-flex px-2 py-0.5 rounded bg-blue-100 text-blue-800 text-xs font-medium">
                  {pa.project?.project_name || `Project #${pa.project_id}`}
                </span>
              ))}
            </div>
          </div>
        )}

        {vendor.activity_associations && vendor.activity_associations.length > 0 && (
          <div className="mt-4">
            <label className="block text-xs font-medium text-gray-600 mb-2">Activities</label>
            <div className="flex flex-wrap gap-1">
              {vendor.activity_associations.map((aa) => (
                <span key={aa.activity_id} className="inline-flex px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">
                  {aa.activity?.activity_name || `Activity #${aa.activity_id}`}
                </span>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Bank & Statutory Details */}
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Bank & Statutory Details</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Vendor Code</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.vendor_code || '-'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">PAN Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900 font-mono">{vendor.pan_number || '-'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Unregistered GST</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.unregistered_gst || '-'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bank Name</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiBriefcase className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900">{vendor.bank_name || '-'}</span>
              </div>
            </div>
          </div>
          <div className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Account Number</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900 font-mono">{vendor.bank_account_number || '-'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">IFSC Code</label>
              <div className="flex items-center gap-2 p-2 bg-gray-50 rounded">
                <FiFileText className="text-gray-400" size={14} />
                <span className="text-sm text-gray-900 font-mono">{vendor.bank_ifsc_code || '-'}</span>
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Bank Address</label>
              <div className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                <FiMapPin className="text-gray-400 mt-0.5" size={14} />
                <span className="text-sm text-gray-900">{vendor.bank_address || '-'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Representatives Tab Component
const RepresentativesTab: React.FC<TabProps> = ({ data }) => {
  const { vendor } = data;

  return (
    <div className="space-y-4">
      <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
        <h3 className="text-base font-semibold text-gray-900 mb-4">Vendor Representatives</h3>

        {vendor.representatives.length === 0 ? (
          <div className="p-8 text-center">
            <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
              <FiUsers className="w-6 h-6 text-gray-400" />
            </div>
            <h3 className="text-base font-medium text-gray-900 mb-1">No representatives added</h3>
            <p className="text-sm text-gray-600 mb-4">Representatives will appear here once they are added.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {vendor.representatives.map((rep) => (
              <div key={rep.id} className="border border-gray-200 rounded p-3 hover:bg-gray-50 transition-colors">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full flex items-center justify-center bg-blue-100 text-blue-700">
                    <FiUser size={16} />
                  </div>

                  <div className="flex-1">
                    <h4 className="text-sm font-medium text-gray-900 mb-1">
                      {rep.contact_person}
                    </h4>

                    <div className="space-y-1">
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiMail size={12} />
                        {rep.email}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiPhone size={12} />
                        {rep.contact_number}
                      </div>
                      <div className="flex items-center gap-1 text-xs text-gray-600">
                        <FiMapPin size={12} />
                        {rep.representative_location}
                      </div>
                      <div className="flex items-center gap-1">
                        <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${rep.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                          {rep.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Users Tab Component
const UsersTab: React.FC<{ vendorId: string }> = ({ vendorId }) => {
  const [users, setUsers] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get('/users/list', {
          params: { vendor_id: vendorId, src: 'all', limit: 200 },
        });
        setUsers(response.data.data?.users || []);
      } catch (err) {
        console.error('Failed to fetch vendor users:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, [vendorId]);

  const roleColors: Record<string, string> = {};
  const palette = [
    'bg-blue-100 text-blue-800',
    'bg-purple-100 text-purple-800',
    'bg-green-100 text-green-800',
    'bg-orange-100 text-orange-800',
    'bg-teal-100 text-teal-800',
    'bg-pink-100 text-pink-800',
  ];
  let colorIdx = 0;
  const getColorForRole = (role: string) => {
    if (!roleColors[role]) {
      roleColors[role] = palette[colorIdx++ % palette.length];
    }
    return roleColors[role];
  };

  if (loading) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 flex justify-center">
        <div className="w-6 h-6 border-2 border-blue-600 border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
        <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
          <FiUsers className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-base font-medium text-gray-900 mb-1">No users found</h3>
        <p className="text-sm text-gray-600">Users associated with this vendor will appear here.</p>
      </div>
    );
  }

  const grouped: Record<string, any[]> = {};
  users.forEach((u) => {
    const roleName = u.role?.name || 'No Role';
    if (!grouped[roleName]) grouped[roleName] = [];
    grouped[roleName].push(u);
  });
  const sortedRoles = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-4">
      {sortedRoles.map((roleName) => {
        const members = [...grouped[roleName]].sort((a, b) =>
          `${a.first_name} ${a.last_name}`.localeCompare(`${b.first_name} ${b.last_name}`)
        );
        const colorClass = getColorForRole(roleName);
        return (
          <div key={roleName} className="bg-white rounded shadow-sm border border-gray-200 p-4">
            <div className="flex items-center gap-2 mb-3">
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${colorClass}`}>
                {roleName}
              </span>
              <span className="text-xs text-gray-500">{members.length} member{members.length !== 1 ? 's' : ''}</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {members.map((u) => (
                <div key={u.id} className="flex items-center gap-3 p-2 border border-gray-100 rounded hover:bg-gray-50 transition-colors">
                  <div className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center text-xs font-bold text-gray-600 shrink-0">
                    {(u.first_name?.[0] || '').toUpperCase()}{(u.last_name?.[0] || '').toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 truncate">
                      {u.first_name} {u.last_name}
                    </p>
                    <p className="text-xs text-gray-500 truncate">{u.email}</p>
                  </div>
                  {u.employee_code && (
                    <span className="text-xs text-gray-400 font-mono shrink-0">{u.employee_code}</span>
                  )}
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Documents Tab Component
const DocumentsTab: React.FC<TabProps> = ({ data }) => {
  const { vendor } = data;
  const docs = vendor.documents || [];

  const formatBytes = (bytes?: number) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (mimeType?: string) => {
    if (!mimeType) return <FiFile className="w-5 h-5 text-gray-400" />;
    if (mimeType.includes('pdf')) return <FiFile className="w-5 h-5 text-red-500" />;
    if (mimeType.includes('image')) return <FiFile className="w-5 h-5 text-blue-500" />;
    if (mimeType.includes('word') || mimeType.includes('document')) return <FiFile className="w-5 h-5 text-blue-700" />;
    if (mimeType.includes('sheet') || mimeType.includes('excel')) return <FiFile className="w-5 h-5 text-green-600" />;
    return <FiFile className="w-5 h-5 text-gray-400" />;
  };

  if (docs.length === 0) {
    return (
      <div className="bg-white rounded shadow-sm border border-gray-200 p-8 text-center">
        <div className="p-3 bg-gray-100 rounded-full w-12 h-12 mx-auto mb-3 flex items-center justify-center">
          <FiBriefcase className="w-6 h-6 text-gray-400" />
        </div>
        <h3 className="text-base font-medium text-gray-900 mb-1">No documents uploaded</h3>
        <p className="text-sm text-gray-600">Documents for this vendor will appear here once added.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h3 className="text-base font-semibold text-gray-900 mb-4">Vendor Documents</h3>
      <div className="divide-y divide-gray-100">
        {docs.map((doc, idx) => (
          <div key={idx} className="flex items-center gap-3 py-3 hover:bg-gray-50 transition-colors rounded px-1">
            <div className="shrink-0">{getFileIcon(doc.mime_type)}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 truncate">{doc.name}</p>
              {doc.size && (
                <p className="text-xs text-gray-500">{formatBytes(doc.size)}</p>
              )}
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <a
                href={doc.url}
                target="_blank"
                rel="noreferrer"
                className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                title="Open"
              >
                <FiExternalLink className="w-4 h-4" />
              </a>
              <a
                href={doc.url}
                download={doc.name}
                className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                title="Download"
              >
                <FiDownload className="w-4 h-4" />
              </a>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// Main VendorView Component
export default function VendorView() {
  const { id } = useParams<{ id: string }>();
  const { hasPermission } = useAuth();
  const canViewContact = hasPermission('VendorManagement', 'view_contact');
  const [vendorData, setVendorData] = useState<VendorData | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [activeTab, setActiveTab] = useState<string>('overview');

  const tabs = [
    { id: 'overview', label: 'Overview', icon: FiInfo },
    // Contact details (representatives) are gated behind a dedicated permission.
    ...(canViewContact ? [{ id: 'representatives', label: 'Contact Details', icon: FiUsers }] : []),
    { id: 'users', label: 'Users', icon: FiUser },
    { id: 'documents', label: 'Documents', icon: FiFile },
  ];

  useEffect(() => {
    const fetchVendor = async () => {
      try {
        setLoading(true);
        const response = await axiosInstance.get(`/vendor/${id}`);
        setVendorData(response.data.data);
      } catch (error) {
        console.error('Error fetching vendor data:', error);
      } finally {
        setLoading(false);
      }
    };
    if (id) fetchVendor();
  }, [id]);

  const renderTabContent = () => {
    if (!vendorData) return null;

    switch (activeTab) {
      case 'overview':
        return <VendorOverviewTab data={vendorData} />;
      case 'representatives':
        return canViewContact ? <RepresentativesTab data={vendorData} /> : <VendorOverviewTab data={vendorData} />;
      case 'users':
        return <UsersTab vendorId={id!} />;
      case 'documents':
        return <DocumentsTab data={vendorData} />;
      default:
        return <VendorOverviewTab data={vendorData} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading vendor details...</p>
        </div>
      </div>
    );
  }

  if (!vendorData) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <FiAlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Vendor Not Found</h2>
          <p className="text-gray-600">The vendor you're looking for doesn't exist or you don't have permission to view it.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="mx-auto">
        {/* Vendor Header */}
        <div className="bg-white rounded shadow-sm border border-gray-200 p-4 mb-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-500 rounded flex items-center justify-center text-white text-sm font-bold">
              {vendorData.vendor.vendor_full_name.substring(0, 2).toUpperCase()}
            </div>

            <div className="flex-1">
              <h1 className="text-xl font-bold text-gray-900">
                {vendorData.vendor.vendor_full_name}
              </h1>
              <p className="text-sm text-gray-600 mb-1">{vendorData.vendor.vendor_short_name}</p>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-500">
                <div className="flex items-center gap-1">
                  <FiFileText size={12} />
                  {vendorData.vendor.gst_number}
                </div>
                <div className="flex items-center gap-1">
                  <FiMapPin size={12} />
                  {vendorData.vendor.state_circle.state_name}
                </div>
                <div className="flex items-center gap-1">
                  <FiCalendar size={12} />
                  Created {new Date(vendorData.vendor.created_at).toLocaleDateString()}
                </div>
                <div className="flex items-center gap-1">
                  <div className={`w-2 h-2 rounded-full ${vendorData.vendor.is_active ? 'bg-green-500' : 'bg-red-500'}`}></div>
                  {vendorData.vendor.is_active ? 'ACTIVE' : 'INACTIVE'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded shadow-sm border border-gray-200 mb-6">
          <div className="border-b border-gray-200">
            <nav className="flex overflow-x-auto">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 text-sm font-medium whitespace-nowrap border-b-2 transition-colors ${
                      activeTab === tab.id
                        ? 'border-blue-600 text-blue-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={16} />
                    {tab.label}
                  </button>
                );
              })}
            </nav>
          </div>

          <div className="p-4">
            {renderTabContent()}
          </div>
        </div>
      </div>
    </div>
  );
}
